import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiGet, apiPatch, apiPost } from '../../../lib/api.js';

const errOf = (e, f) => e?.response?.data?.error || f;

/**
 * Mutations + the Pending Deletion list for the My Phone Numbers lifecycle.
 * @returns {{pending: Object[], setDefault: Function, setStatus: Function, setSpecial: Function, detach: Function, remove: Function, restore: Function, busy: boolean}}
 */
export default function usePhoneLifecycle() {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['phones-owned'] });
    qc.invalidateQueries({ queryKey: ['phones-pending-deletion'] });
  };
  const opts = (ok, fail) => ({ onSuccess: (r) => { refresh(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); }, onError: (e) => toast.error(errOf(e, fail)) });

  const { data } = useQuery({
    queryKey: ['phones-pending-deletion'],
    queryFn: () => apiGet('/phones/pending-deletion').catch(() => ({ numbers: [] })),
    staleTime: 15_000,
  });

  const setDefault = useMutation({ mutationFn: (id) => apiPatch(`/phones/${id}/default`), ...opts('Default number updated', 'Could not set default') });
  const setStatus = useMutation({ mutationFn: ({ id, status }) => apiPatch(`/phones/${id}/status`, { status }), ...opts((r) => (r.phone?.status === 'inactive' ? 'Number set to inactive (inbound only)' : 'Number is active again'), 'Could not change status') });
  const setSpecial = useMutation({ mutationFn: ({ id, isSpecial }) => apiPatch(`/phones/${id}/special`, { isSpecial }), ...opts((r) => (r.phone?.isSpecial ? 'Special number enabled' : 'Special number removed'), 'Could not update special number') });
  const detach = useMutation({ mutationFn: (id) => apiPost(`/phones/${id}/detach-leads`), ...opts((r) => `Detached from ${r.detached} lead${r.detached === 1 ? '' : 's'}`, 'Could not detach leads') });
  const remove = useMutation({
    mutationFn: ({ id, retain30Days }) => apiPost(`/phones/${id}/delete`, { retain30Days }),
    ...opts((r) => (r.retained ? (r.feeUsd ? 'Number moved to Pending Deletion — $1.00 charged' : 'Number moved to Pending Deletion — no charge') : 'Number permanently deleted'), 'Could not delete the number'),
  });
  const extend = useMutation({ mutationFn: (id) => apiPost(`/phones/${id}/extend`), ...opts('Retention extended 30 days — $1.00 charged', 'Could not extend retention') });
  const restore = useMutation({ mutationFn: (id) => apiPost(`/phones/${id}/restore`), ...opts('Number restored', 'Could not restore the number') });

  const busy = [setDefault, setStatus, setSpecial, detach, remove, restore, extend].some((m) => m.isPending);
  return { pending: data?.numbers || [], setDefault, setStatus, setSpecial, detach, remove, restore, extend, busy };
}
